"use client"

import { useCallback, useMemo, useState } from "react"
import { Layers, Search, X, Eye, EyeOff, Plus, Minus, ChevronDown, ChevronUp, Table2 } from "lucide-react"
import { Input } from "@/components/ui/input"
import { useTranslation } from "@/hooks/use-translation"
import { useLocalizedField } from "@/utils/language-utils"
import { useWorkflow } from "@/context/workflow-context-simple"
import { useViewManager } from "../view-manager-context"
import { TablePreview } from "../preview/table-preview"
import { ColumnReorderList } from "../panels/column-reorder-list"
import { FieldEditPanel, fieldEditDirtyRef } from "../panels/field-edit-panel"

export function FieldsStep() {
  const { t } = useTranslation()
  const { state, dispatch } = useViewManager()
  const { configuration } = useWorkflow()
  const getLocalizedField = useLocalizedField()
  const [hiddenSearch, setHiddenSearch] = useState('')
  const showPreview = state.showPreview
  const setShowPreview = (v: boolean) => dispatch({ type: 'SET_SHOW_PREVIEW', payload: v })

  // Permissions
  const authorizedActions = (configuration?.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode)
  const canEditColumns = authorizedActions.includes("EditColumns")

  const stages = state.originalConfig?.stageConfigurations || []
  const fieldsInUse = state.originalConfig?.fieldsInUse || []
  const activeStage = state.activeEditStage
  const visibleColumnIds = state.draftStageColumns[activeStage] || []

  // Build field map
  const allFieldsMap = useMemo(() => {
    const map = new Map<string, any>()
    for (const f of fieldsInUse) map.set(f.fieldConfigurationId, f)
    for (const s of stages) {
      for (const f of (s.fieldConfigurations || [])) map.set(f.fieldConfigurationId, f)
    }
    return map
  }, [fieldsInUse, stages])

  // Hidden columns (alphabetically sorted)
  const hiddenFields = useMemo(() => {
    return Array.from(allFieldsMap.values())
      .filter(f => !visibleColumnIds.includes(f.fieldConfigurationId))
      .sort((a, b) => {
        const nameA = (getLocalizedField(a, 'columnName') || a.columnCode || '').toLowerCase()
        const nameB = (getLocalizedField(b, 'columnName') || b.columnCode || '').toLowerCase()
        return nameA.localeCompare(nameB)
      })
  }, [allFieldsMap, visibleColumnIds, getLocalizedField])

  const filteredHidden = useMemo(() => {
    if (!hiddenSearch.trim()) return hiddenFields
    const q = hiddenSearch.toLowerCase()
    return hiddenFields.filter(f => {
      const name = getLocalizedField(f, 'columnName') || f.columnCode || ''
      return name.toLowerCase().includes(q)
    })
  }, [hiddenFields, hiddenSearch, getLocalizedField])

  // Filter visible columns by the same search term
  const filteredVisibleIds = useMemo(() => {
    if (!hiddenSearch.trim()) return visibleColumnIds
    const q = hiddenSearch.toLowerCase()
    return visibleColumnIds.filter(id => {
      const f = allFieldsMap.get(id)
      if (!f) return false
      const name = getLocalizedField(f, 'columnName') || f.columnCode || ''
      return name.toLowerCase().includes(q)
    })
  }, [visibleColumnIds, hiddenSearch, allFieldsMap, getLocalizedField])

  // Select a field — warn if current field has unsaved changes
  const handleFieldSelect = useCallback((fieldId: string) => {
    if (fieldEditDirtyRef.current && fieldId !== state.selectedFieldId) {
      const confirmed = window.confirm('You have unsaved field changes. Discard them?')
      if (!confirmed) return
    }
    dispatch({ type: 'SELECT_FIELD', payload: fieldId === state.selectedFieldId ? null : fieldId })
  }, [dispatch, state.selectedFieldId])

  const handleShowColumn = (fieldId: string) => {
    dispatch({ type: 'TOGGLE_COLUMN', payload: { stageId: activeStage, fieldId } })
  }

  if (!state.selectedView) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-sm">
        {t('vmSelectViewFirst' as any) || 'Select a view from the Views tab to configure fields'}
      </div>
    )
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left: Stage tabs */}
      <div className="w-52 border-r border-gray-200 bg-gradient-to-b from-[#F4F3FF] to-[#FAFAFF] flex-shrink-0 p-4 overflow-y-auto">
        <div className="flex items-center gap-2 mb-4">
          <Layers size={16} className="text-[#0F214B]" />
          <h3 className="text-sm font-bold text-[#0F214B]">
            {t('stages' as any) || 'Stages'}
          </h3>
        </div>
        <p className="text-[11px] text-gray-400 mb-3 leading-relaxed">
          Drag to reorder visible columns. Move columns between visible and hidden panels.
        </p>
        <div className="space-y-1.5">
          {stages.map((stage: any) => {
            const stageId = stage.stageId || stage.stageCode
            const title = getLocalizedField(stage, 'title') || stage.stageCode
            const isActive = activeStage === stageId
            return (
              <button
                key={stageId}
                onClick={() => {
                  if (fieldEditDirtyRef.current) {
                    const confirmed = window.confirm('You have unsaved field changes. Discard them?')
                    if (!confirmed) return
                    fieldEditDirtyRef.current = false
                  }
                  dispatch({ type: 'SET_ACTIVE_STAGE', payload: stageId })
                  dispatch({ type: 'SELECT_FIELD', payload: null })
                }}
                className={`
                  w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                  ${isActive
                    ? 'bg-white text-[#0F214B] shadow-md border border-blue-200 ring-1 ring-blue-100'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-white/60 border border-transparent'
                  }
                `}
              >
                {title}
              </button>
            )
          })}
        </div>
      </div>

      {/* Center: Side-by-side layout */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Table preview (collapsible, full width — matches columns-step exactly) */}
        <div className="flex-shrink-0 border-b border-gray-200">
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-2 text-sm font-semibold text-[#0F214B] hover:text-blue-700 transition-colors"
            >
              {showPreview ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              <Table2 size={15} />
              {t('vmColumnPreview' as any) || 'Table Preview'}
              <span className="font-normal text-gray-400 text-xs">
                — {getLocalizedField(stages.find((s: any) => (s.stageId || s.stageCode) === activeStage) || {}, 'title') || activeStage}
              </span>
            </button>
          </div>
          {showPreview && (
            <div>
              <TablePreview selectedColumnId={state.selectedFieldId} onColumnSelect={handleFieldSelect} />
            </div>
          )}
        </div>

        {/* Unified search + Side-by-side columns */}
        <div className="flex-1 flex flex-col overflow-hidden border-t border-gray-200">
          {/* Unified search bar */}
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex-shrink-0">
            <div className="relative max-w-md">
              <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />
              <Input
                placeholder="Search all columns..."
                className="pl-8 pr-7 h-8 text-xs"
                value={hiddenSearch}
                onChange={(e) => setHiddenSearch(e.target.value)}
              />
              {hiddenSearch && (
                <button onClick={() => setHiddenSearch('')} className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600">
                  <X size={12} />
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 flex overflow-hidden">
          {/* Hidden columns (left) */}
          <div className="w-72 flex flex-col overflow-hidden flex-shrink-0 border-r border-gray-200">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2 flex-shrink-0">
              <EyeOff size={14} className="text-gray-400" />
              <h4 className="text-xs font-semibold text-[#0F214B]">
                {t('hiddenColumns') || 'Hidden Columns'}
              </h4>
              <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded-full font-semibold">
                {filteredHidden.length}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              {filteredHidden.length === 0 ? (
                <p className="text-xs text-gray-400 py-4 text-center">
                  {hiddenSearch ? 'No matches' : 'All columns visible'}
                </p>
              ) : (
                <div className="space-y-1">
                  {filteredHidden.map(field => {
                    const name = getLocalizedField(field, 'columnName') || field.columnCode || field.fieldConfigurationId
                    const isSelected = state.selectedFieldId === field.fieldConfigurationId
                    return (
                      <div
                        key={field.fieldConfigurationId}
                        className={`flex items-center justify-between px-2.5 py-1.5 rounded border transition-colors cursor-pointer group ${
                          isSelected
                            ? 'bg-blue-50 border-blue-200'
                            : 'border-gray-100 bg-white hover:bg-gray-50 hover:border-gray-200'
                        }`}
                        onClick={() => handleFieldSelect(field.fieldConfigurationId)}
                      >
                        <EyeOff size={12} className="text-gray-300 flex-shrink-0 mr-2" />
                        <span className="text-xs text-gray-600 truncate flex-1">{name}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleShowColumn(field.fieldConfigurationId) }}
                          className="p-0.5 rounded hover:bg-green-100 text-gray-300 hover:text-green-600 flex-shrink-0"
                          title="Add to visible columns"
                        >
                          <Eye size={13} />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Visible columns (right) — drag to reorder */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2 flex-shrink-0">
              <Eye size={14} className="text-green-600" />
              <h4 className="text-xs font-semibold text-[#0F214B]">
                {t('visibleColumns') || 'Visible Columns'}
              </h4>
              <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full font-semibold">
                {hiddenSearch ? `${filteredVisibleIds.length}/${visibleColumnIds.length}` : visibleColumnIds.length}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <ColumnReorderList
                selectedFieldId={state.selectedFieldId}
                onFieldSelect={handleFieldSelect}
                filterIds={hiddenSearch.trim() ? filteredVisibleIds : undefined}
              />
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Right: Editable field side panel */}
      {state.selectedFieldId && (
        <FieldEditPanel canEdit={canEditColumns} />
      )}
    </div>
  )
}
